import React, { useState } from 'react';
import EmailTemplateThumbnail from './EmailTemplateThumbnail';

/**
 * EmailTemplateRow
 *
 * A single row in the email templates table view, displaying:
 * - Thumbnail preview of the default template
 * - Email type name with badges (trigger type, paused state)
 * - Last updated timestamp
 * - Edit button, pause toggle, and actions menu
 */
const EmailTemplateRow = ({ emailType, onEdit, onCreateTemplate, onToggleArchive, onRename, onTogglePause }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const { default_template, name, description, templates_count } = emailType;

  // Format relative time (e.g., "3 months ago")
  const formatRelativeTime = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffYears > 0) return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    if (diffMonths > 0) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    if (diffWeeks > 0) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const triggerLabel = (type) => {
    switch (type) {
      case 'event_driven': return 'Event-Driven';
      case 'scheduled_daily': return 'Daily';
      case 'scheduled_monthly': return 'Monthly';
      default: return null;
    }
  };

  const hasTemplate = !!default_template;
  const lastUpdated = hasTemplate
    ? formatRelativeTime(default_template.updated_at)
    : null;
  const updatedBy = hasTemplate ? (default_template.updated_by || 'Admin') : null;
  const isPaused = emailType.paused;
  const trigger = triggerLabel(emailType.trigger_type);

  return (
    <div className={`email-template-row ${isPaused ? 'email-template-row--paused' : ''}`}>
      {/* Thumbnail */}
      <div className="row-thumbnail">
        <EmailTemplateThumbnail
          htmlContent={default_template?.html_content}
          width={100}
          height={130}
        />
      </div>

      {/* Content */}
      <div className="row-content">
        <div className="row-header">
          {isRenaming ? (
            <div className="rename-inline">
              <input
                type="text"
                className="rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const trimmed = renameValue.trim();
                    if (trimmed && trimmed !== name) {
                      onRename(emailType, trimmed);
                    }
                    setIsRenaming(false);
                  } else if (e.key === 'Escape') {
                    setIsRenaming(false);
                  }
                }}
                autoFocus
              />
              <button
                className="rename-btn rename-btn--save"
                onClick={() => {
                  const trimmed = renameValue.trim();
                  if (trimmed && trimmed !== name) {
                    onRename(emailType, trimmed);
                  }
                  setIsRenaming(false);
                }}
                title="Save"
              >
                <i className="fa fa-check"></i>
              </button>
              <button
                className="rename-btn rename-btn--cancel"
                onClick={() => setIsRenaming(false)}
                title="Cancel"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
          ) : (
            <h3 className="row-title">{name}</h3>
          )}
          <div className="row-badges">
            <span className="badge badge-type">Email</span>
            {hasTemplate && !isPaused && <span className="badge badge-default">Active</span>}
            {isPaused && <span className="badge badge-paused">Paused</span>}
            {trigger && <span className="badge badge-trigger">{trigger}</span>}
          </div>
        </div>

        {description && (
          <p className="row-description">{description}</p>
        )}

        <div className="row-meta">
          {hasTemplate ? (
            <span className="meta-text">
              Last updated: {lastUpdated} by {updatedBy}
            </span>
          ) : (
            <span className="meta-text meta-text--empty">
              No template yet
            </span>
          )}
          {templates_count > 0 && (
            <span className="meta-count">
              {templates_count} template{templates_count > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="row-actions">
        {hasTemplate ? (
          <button
            className="btn-edit"
            onClick={() => onEdit(emailType)}
          >
            Edit
          </button>
        ) : (
          <button
            className="btn-create"
            onClick={() => onCreateTemplate(emailType)}
          >
            + Create
          </button>
        )}

        <div className="actions-menu-container">
          <button
            className="btn-menu"
            onClick={() => setShowMenu(!showMenu)}
            onBlur={() => setTimeout(() => setShowMenu(false), 150)}
          >
            <i className="fa fa-ellipsis-v"></i>
          </button>

          {showMenu && (
            <div className="actions-menu">
              <button
                className="menu-item"
                onClick={() => {
                  setShowMenu(false);
                  onEdit(emailType);
                }}
              >
                <i className="fa fa-edit"></i>
                Manage Templates
              </button>
              {templates_count > 1 && (
                <button
                  className="menu-item"
                  onClick={() => {
                    setShowMenu(false);
                    onEdit(emailType);
                  }}
                >
                  <i className="fa fa-list"></i>
                  View All ({templates_count})
                </button>
              )}
              <button
                className="menu-item"
                onClick={() => {
                  setShowMenu(false);
                  setRenameValue(name);
                  setIsRenaming(true);
                }}
              >
                <i className="fa fa-pencil-alt"></i>
                Rename
              </button>
              <button
                className="menu-item"
                onClick={() => {
                  setShowMenu(false);
                  onTogglePause(emailType);
                }}
              >
                <i className={`fa ${isPaused ? 'fa-play' : 'fa-pause'}`}></i>
                {isPaused ? 'Resume Sending' : 'Pause Sending'}
              </button>
              <button
                className="menu-item"
                onClick={() => {
                  setShowMenu(false);
                  onToggleArchive(emailType);
                }}
              >
                <i className={`fa ${emailType.active ? 'fa-archive' : 'fa-undo'}`}></i>
                {emailType.active ? 'Archive' : 'Unarchive'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateRow;
